'use client';
import { jwtDecode } from 'jwt-decode';
import { useRouter } from 'next/navigation';
import type { User } from 'oidc-client-ts';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Market } from '@//config/domain-locale-map';
import type { Token } from '@//Token';
import { createUserManager, getUserManagerAsync } from './userManager';

const REDIRECT_PATH_SIGN_IN = '/oidc/signin';
const REDIRECT_PATH_SIGN_OUT = '/oidc/signout';
const REDIRECT_PATH_REMOVE_ACCOUNT = '/oidc/remove-account';
const REDIRECT_URL_KEY = 'redirectUrl';
const REQUIRED_SCOPES = 'openid profile api-resource offline_access'.split(' ').sort().join(' ');

const currentUrlIsRedirectSignInUrl = () =>
  window.location.pathname.startsWith(REDIRECT_PATH_SIGN_IN);
const currentUrlIsRedirectSignOutUrl = () =>
  window.location.pathname.startsWith(REDIRECT_PATH_SIGN_OUT);
const currentUrlIsRemoveAccountRedirectUrl = () =>
  window.location.pathname.startsWith(REDIRECT_PATH_REMOVE_ACCOUNT);

const verifyScopes = (userScopes?: string) =>
  userScopes?.split(' ').sort().join(' ') === REQUIRED_SCOPES;

const signInRedirect = async (redirectUrl?: string) => {
  // signinRedirect first fetches OIDC metadata from auth.wayke.se; that fetch
  // can fail at the network level (offline, a backgrounded tab waking, an IdP
  // blip), and sessionStorage can throw in privacy-restricted browsers. There
  // is nothing to recover — the redirect simply cannot start right now — so
  // swallow it rather than leak an unhandled rejection through the several
  // fire-and-forget callers. A later interaction will retry.
  try {
    sessionStorage.setItem(
      REDIRECT_URL_KEY,
      redirectUrl
        ? redirectUrl
        : window.location.pathname === REDIRECT_PATH_SIGN_IN
          ? '/'
          : window.location.pathname,
    );
    const userManager = await getUserManagerAsync();
    await userManager.signinRedirect();
  } catch (error) {
    console.warn('Auth: could not start sign-in redirect', error);
  }
};

const signOutRedirect = async (id_token_hint?: string, redirectUrl?: string) => {
  try {
    sessionStorage.setItem(
      REDIRECT_URL_KEY,
      redirectUrl
        ? redirectUrl
        : window.location.pathname === REDIRECT_PATH_SIGN_IN
          ? '/'
          : window.location.pathname,
    );
    const userManager = await getUserManagerAsync();
    await userManager.signoutRedirect({
      id_token_hint,
    });
  } catch (error) {
    console.warn('Auth: could not start sign-out redirect', error);
  }
};

const signInSilent = async (autoRedirectSignIn = true) => {
  try {
    const userManager = await getUserManagerAsync();
    const user = await userManager.signinSilent();
    if (!user || !verifyScopes(user.scope)) {
      if (autoRedirectSignIn) {
        signInRedirect();
      }
    }
    return user || undefined;
  } catch (_e) {
    if (autoRedirectSignIn) {
      signInRedirect();
    }
    return undefined;
  }
};

const getUser = async (autoRedirectSignIn = true, redirectUrl?: string) => {
  try {
    const userManager = await getUserManagerAsync();
    const user = await userManager.getUser();
    if ((!user || !verifyScopes(user.scope)) && autoRedirectSignIn) {
      signInRedirect(redirectUrl);
      return undefined;
    }

    if (user?.expired) {
      const refreshedUser = await signInSilent(autoRedirectSignIn);
      return refreshedUser;
    }

    return user || undefined;
  } catch (_e) {
    if (autoRedirectSignIn) {
      signInRedirect(redirectUrl);
    }
    return undefined;
  }
};

export interface AuthContextProps {
  userManagerInitialized: boolean;
  initialized: boolean;
  authResolved: boolean;
  decodedToken?: Token;
  signOut: (redirectUrl?: string) => Promise<void>;
  getToken: () => Promise<string | undefined>;
  signIn: (redirectUrl?: string) => void;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

let tokenPromise: Promise<string | undefined> | null = null;

export const getToken = async () => {
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = new Promise(async (resolve) => {
    try {
      const user = await getUser();
      resolve(user?.access_token);
    } catch (_e) {
      resolve(undefined);
    } finally {
      tokenPromise = null;
    }
  });

  return tokenPromise;
};

const signOut = async (redirectUrl?: string) => {
  let id_token_hint: string | undefined;
  const userManager = await getUserManagerAsync();
  try {
    const user = await userManager.getUser();
    id_token_hint = user?.id_token;
  } catch (_e) {
    id_token_hint = undefined;
  }
  await signOutRedirect(id_token_hint, redirectUrl);

  /*
  userManager.signoutRedirect({
    id_token_hint,
  });
  */
};

const getDecodedToken = (token?: string) => {
  const decodedToken = token ? jwtDecode<Token>(token) : undefined;
  if (!decodedToken) return undefined;

  const transformBranches =
    decodedToken['belongs-to-branch'] && typeof decodedToken['belongs-to-branch'] === 'string';

  return {
    ...decodedToken,
    'belongs-to-branch': transformBranches
      ? [decodedToken['belongs-to-branch'] as unknown as string]
      : decodedToken['belongs-to-branch'],
  };
};

// For debug
/*
window.waykeAuth = {
  refreshToken: () => signInSilent(),
  getToken: () => getToken(),
  signOut: () => signOut(),
};
*/

interface AuthProviderProps {
  market: Market;
  autoRedirectSignIn?: boolean;
  children: React.ReactNode;
}

const AuthProvider = ({ market, autoRedirectSignIn = false, children }: AuthProviderProps) => {
  const navigate = useRouter();
  const [token, setToken] = useState<string | undefined>();
  const [userManagerInitialized, setUserManagerInitialized] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    const userManager = await createUserManager(market);
    userManager.clearStaleState();
    setUserManagerInitialized(true);
    userManager.events.addUserLoaded((user: User) => {
      setToken(user.access_token);
    });

    userManager.events.addSilentRenewError(async () => {
      signInRedirect();
    });

    if (currentUrlIsRedirectSignInUrl()) {
      try {
        const user = await userManager.signinCallback();
        setToken(user?.access_token);
        setAuthResolved(true);

        const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY) || '/';
        sessionStorage.removeItem(REDIRECT_URL_KEY);
        navigate.push(redirectUrl);
      } catch (_e) {
        setAuthResolved(true);
        signOut();
      }
    } else if (currentUrlIsRemoveAccountRedirectUrl()) {
      await userManager.signoutCallback();
      setAuthResolved(true);
      const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY) || '/';
      sessionStorage.removeItem(REDIRECT_URL_KEY);
      navigate.replace(redirectUrl);
    } else {
      const user = await getUser(autoRedirectSignIn);
      setToken(user?.access_token);
      setUserManagerInitialized(true);
      setAuthResolved(true);
    }
    if (currentUrlIsRedirectSignOutUrl()) {
      await userManager.signoutCallback();
      setAuthResolved(true);
      const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY) || '/';
      sessionStorage.removeItem(REDIRECT_URL_KEY);
      navigate.replace(redirectUrl);
    }
  };

  const signIn = async (redirectUrl?: string) => {
    const user = await getUser(true, redirectUrl);
    setToken(user?.access_token);
  };

  const decodedToken = getDecodedToken(token);

  return (
    <AuthContext.Provider
      value={{
        userManagerInitialized,
        initialized: !!token,
        authResolved,
        decodedToken,
        signOut,
        getToken,
        signIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};

export default AuthProvider;
